import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  X,
  XCircle,
  CheckCircle,
  AlertCircle,
  MinusCircle,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import { cn, formatBytes } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useUploadStore } from '@/store/uploadStore';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useState } from 'react';

const GlobalUploadProgressPanel = () => {
  const { files, cancelUpload, removeFile, clearCompleted } = useUploadStore();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const fileEntries = Object.entries(files);
  if (fileEntries.length === 0) return null;

  const activeUploads = fileEntries.filter(
    ([_, file]) => file.status === 'queued' || file.status === 'uploading'
  ).length;

  const completedUploads = fileEntries.filter(
    ([_, file]) => file.status === 'completed'
  ).length;

  return (
    <Card className="fixed bottom-4 right-4 z-50 w-[500px] shadow-lg">
      <CardHeader
        className={cn(
          'flex flex-row items-center justify-between pb-2',
          isCollapsed && 'pt-2'
        )}
      >
        <CardTitle className="text-base">
          Uploading Files ({fileEntries.length})
          {activeUploads > 0 && ` - ${activeUploads} active`}
        </CardTitle>
        <div className="flex gap-2">
          {completedUploads > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearCompleted}
              className="h-7 px-2"
            >
              Clear completed
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="h-7 w-7 p-0"
          >
            {isCollapsed ? (
              <ChevronUp className="size-4" />
            ) : (
              <ChevronDown className="size-4" />
            )}
            <span className="sr-only">
              {isCollapsed ? 'Expand' : 'Collapse'}
            </span>
          </Button>
        </div>
      </CardHeader>
      {!isCollapsed && (
        <CardContent>
          <ScrollArea className="h-48 w-full pr-3">
            <div className="flex flex-col gap-4">
              {fileEntries.map(
                ([
                  fileKey,
                  { file, path: filePath, progress, speed, status },
                ]) => {
                  // สร้าง icon ตามสถานะ
                  let StatusIcon = null;
                  let statusColor = '';

                  switch (status) {
                    case 'completed':
                      StatusIcon = CheckCircle;
                      statusColor = 'text-green-500';
                      break;
                    case 'error':
                      StatusIcon = AlertCircle;
                      statusColor = 'text-red-500';
                      break;
                    case 'cancelled':
                      StatusIcon = MinusCircle;
                      statusColor = 'text-yellow-500';
                      break;
                    default:
                      StatusIcon = null;
                  }

                  return (
                    <div
                      key={fileKey}
                      className="relative flex items-center gap-2.5"
                    >
                      <div className="flex flex-1 gap-2.5">
                        <div className="flex w-full flex-col gap-2">
                          <div className="flex items-center justify-between">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex flex-col">
                                    <div className="flex items-center gap-2">
                                      {StatusIcon && (
                                        <StatusIcon
                                          className={`size-4 ${statusColor}`}
                                          aria-hidden="true"
                                        />
                                      )}
                                      <span className="text-sm font-medium">
                                        {file.name}
                                      </span>
                                    </div>

                                    <p className="text-xs text-muted-foreground">
                                      {formatBytes(file.size)}
                                      {status === 'completed' && ' • Completed'}
                                      {status === 'uploading' &&
                                        speed &&
                                        ` • ${speed}`}
                                      {status === 'queued' && ' • queued'}
                                      {status === 'error' && ' • Failed'}
                                      {status === 'cancelled' && ' • Cancelled'}
                                    </p>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{filePath}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>

                            {status !== 'completed' &&
                            status !== 'error' &&
                            status !== 'cancelled' ? (
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="size-7"
                                onClick={() => cancelUpload(fileKey)}
                              >
                                <X className="size-4" aria-hidden="true" />
                                <span className="sr-only">Cancel upload</span>
                              </Button>
                            ) : (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="size-7"
                                onClick={() => removeFile(fileKey)}
                              >
                                <X className="size-4" aria-hidden="true" />
                                <span className="sr-only">
                                  Remove from list
                                </span>
                              </Button>
                            )}
                          </div>
                          <Progress value={progress} />
                        </div>
                      </div>
                    </div>
                  );
                }
              )}
            </div>
          </ScrollArea>
        </CardContent>
      )}
    </Card>
  );
};

export default GlobalUploadProgressPanel;
